/**
 *
 * Password
 * See also https://css-tricks.com/password-strength-meter/
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styled from 'styled-components';
import { omit } from 'lodash-es';
import { FormattedMessage, useIntl } from 'react-intl';
import { Alert } from 'react-bootstrap';
import FormControlWrapper from 'components/interaction/Form4/FormControlWrapper.jsx';
import { intlTags } from 'utils/intlFunctions.jsx';
import { object, string } from 'yup';
import messages from './messages.js';
import Meter from './Meter.js';
import useStrength, { STRENGTH_EMPTY } from './useStrength.js';
import PasswordInput from './PasswordInput.jsx';
import { PASSWORDLENGTH } from './constants.js';

const MeterDiv = styled.div`
  meter {
    height: 14px;
    border-radius: 6px;
    overflow: hidden;
  }
  span {
    text-align: right;
    font-size: 12px;
    flex-basis: 5em;
  }
`;

function Password(props) {
  const {
    minLength = PASSWORDLENGTH, update, id, userData = {}, isAdmin, hint, className, onChange, onBlur, ...rest
  } = props;
  const [value, updateValue] = useState({ current: '', password: '', retype: '', visible: false, strength: STRENGTH_EMPTY });
  const intl = useIntl();
  const scoreTexts = intl.formatMessage(messages.texts).split('|');
  const lengthProps = minLength ? {
    pattern: `(.){${minLength},}`,
    title: intl.formatMessage(messages.minLength, { min: minLength }),
  } : {};

  const strength = useStrength(value.password, isAdmin, userData);

  useEffect(() => {
    handleChange('strength', strength);
  }, [strength]);

  const toggleVisible = (name) => () => {
    document.querySelector(`[name=${name}]`).focus();
    handleChange('visible', !value.visible);
  };

  function handleChange(key, val) {
    updateValue((oldValue) => {
      const newValue = { ...oldValue, [key]: val };
      setTimeout(() => onChange(newValue), 0);
      return newValue;
    });
  }

  const { visible } = value;
  const inputOptions = { ...omit(rest, ['value', 'onPopup']), className: 'mb-3', visible, minLength, onBlur };
  function getOptions(key) {
    return {
      ...inputOptions,
      id: key,
      onChange: (ev) => handleChange(key, ev.target.value),
      message: messages[key],
      value: value[key],
    };
  }
  const classList = classNames(['w-100', className]);

  return (
    <FormControlWrapper className={classList} border={2}>
      {update && (
        <PasswordInput
          {...getOptions('current')}
          setVisible={toggleVisible('current')}
        />
      )}
      <PasswordInput
        {...getOptions('password')}
        {...lengthProps}
        showLength
        hint={hint}
        message={update ? messages.new : messages.password}
        setVisible={toggleVisible('password')}
      />
      {!visible && (
        <PasswordInput
          {...getOptions('retype')}
          pattern={escapeRegExp(value.password || '')}
          title={intl.formatMessage(messages.noMatch)}
        />
      )}
      {strength.score >= 0 && (
        <>
          {strength.message && (
            <Alert variant="danger" className="py-3">{strength.message}</Alert>
          )}
          <MeterDiv className="d-flex align-items-center my-3 w-100">
            <Meter className="flex-grow-1 me-3" max="4" value={strength.score} id="password-strength-meter"></Meter>
            <span id="password-strength-text">{scoreTexts[strength.score]}</span>
          </MeterDiv>
          {strength.score <= 2 && (
            <Alert variant="info" className="narrow pb-2"><FormattedMessage {...messages.weakPasswordFull} values={intlTags(['p', 'ul', 'li'])} /></Alert>
          )}
        </>
      )}
    </FormControlWrapper>
  );
}

Password.propTypes = {
  minLength: PropTypes.number,
  hint: PropTypes.object,
  intl: PropTypes.object,
  id: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  update: PropTypes.bool,
  isAdmin: PropTypes.bool,
  userData: PropTypes.object,
  className: PropTypes.string,
};

Password.getSchema = (options, intl) => {
  const { update, attributes } = options;
  return object().shape({
    current: update ? string().trim().required().label(intl.formatMessage(messages.current)) : undefined,
    password: string().trim().required().min(attributes?.minLength || PASSWORDLENGTH).label(intl.formatMessage(messages.password)),
  }).default({})
    .test('strength', () => intl.formatMessage(messages.weakPassword), (value) => value.strength?.valid)
    .test('match', () => intl.formatMessage(messages.noMatch), (value) => value.visible || value.password === value.retype);
};

export default Password;

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions
function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}
